import { useMemo, useState } from 'react';

import type { IdAdTypeDto, IdPlacementDto } from '@/services/ws-point-of-sale/src/main/resources/rest-api';
import { getAdTypes } from '@/services/ws-point-of-sale/src/main/resources/rest-api';
import { useSubscribe } from '@/js/libs/observable';
import { NotActive, useRequest } from '@/js/libs/use-request';

import { DEFAULT_DEBOUNCE_DELAY, DROP_DOWN_ENTITY_LIMIT } from 'src/constants';
import type { Option } from 'src/types';

type UsePosAdTypeOptionsParams = {
  placementId: IdPlacementDto | undefined;
};

type UsePosAdTypeOptionsReturn = {
  options: Option<IdAdTypeDto>[];
  pending: boolean;
  total: number;
  searchFilter: string;
  setSearchFilter: (filter: string) => void;
};

export const usePosAdTypeOptions = ({ placementId }: UsePosAdTypeOptionsParams): UsePosAdTypeOptionsReturn => {
  const [searchFilter, setSearchFilter] = useState<string>('');

  const getAdTypeListRequest = useRequest({
    apiMethod: getAdTypes,
    params: placementId
      ? {
          adTypeFilterParams: {
            adTypeFilter: { placementId, nameFilter: searchFilter },
            paginationParams: { limit: DROP_DOWN_ENTITY_LIMIT, page: 0 },
          },
        }
      : NotActive,
    debounce: DEFAULT_DEBOUNCE_DELAY,
  });

  const options = useSubscribe(
    getAdTypeListRequest.data$,
    (response) =>
      response?.data.items.map((item) => ({
        label: item.name,
        value: item.id,
      })) ?? [],
  );
  const total = useSubscribe(getAdTypeListRequest.data$, (result) => result?.data?.total ?? 0);
  const pending = useSubscribe(getAdTypeListRequest.pending$);

  return useMemo(
    () => ({
      options,
      pending,
      total,
      searchFilter,
      setSearchFilter,
    }),
    [options, pending, total, searchFilter],
  );
};
